function escapeHTML(str) {
  if (str == null) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function template(str, vars) {
  return Object.entries(vars).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, String(v)), str);
}

const ICON_MINUS = '<svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.3438 8.3125H2.34375C2.08487 8.3125 1.875 8.10263 1.875 7.84375C1.875 7.58487 2.08487 7.375 2.34375 7.375H12.3438C12.6026 7.375 12.8125 7.58487 12.8125 7.84375C12.8125 8.10263 12.6026 8.3125 12.3438 8.3125Z" fill="currentColor"/></svg>';
const ICON_PLUS = '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.3438 5.375H6.8125V0.84375C6.8125 0.584867 6.60263 0.375 6.34375 0.375C6.08487 0.375 5.875 0.584867 5.875 0.84375V5.375H1.34375C1.08487 5.375 0.875 5.58487 0.875 5.84375C0.875 6.10263 1.08487 6.3125 1.34375 6.3125H5.875V10.8438C5.875 11.1026 6.08487 11.3125 6.34375 11.3125C6.60263 11.3125 6.8125 11.1026 6.8125 10.8438V6.3125H11.3438C11.6026 6.3125 11.8125 6.10263 11.8125 5.84375C11.8125 5.58487 11.6026 5.375 11.3438 5.375Z" fill="currentColor"/></svg>';

class CalculadoraRaciones extends HTMLElement {
  constructor() {
    super();
    this.state = this._initialState();
    this.data = null;
    this.strings = null;
    this._flickity = null;
  }

  _initialState() {
    return {
      step: 1,
      etapa: null,
      peso: null,
      edadCachorro: null,
      periodo: null,
      gramosDia: null,
      gramosTotal: null,
      clampInfo: null,
      variantWeights: new Map(),
      carritoLocal: new Map(),
    };
  }

  connectedCallback() {
    this.data = JSON.parse(this.querySelector("[data-calc-data]").textContent);
    this.strings = JSON.parse(this.querySelector("[data-calc-strings]").textContent);
    this.body = this.querySelector("[data-calc-body]");

    this.addEventListener("click", (e) => {
      if (e.target.closest("[data-calc-close]")) this.close();
      if (e.target.closest("[data-calc-restart]")) this.reset();
    });

    this.renderStep1();
  }

  open() {
    this.removeAttribute("hidden");
    document.body.classList.add("calc-popup-open");
  }

  close() {
    this.setAttribute("hidden", "");
    document.body.classList.remove("calc-popup-open");
  }

  reset() {
    if (this._flickity) {
      this._flickity.destroy();
      this._flickity = null;
    }
    this.state = this._initialState();
    this.setAttribute("data-active-step", "1");
    this.renderStep1();
  }

  goToStep(n) {
    this.state.step = n;
    this.setAttribute("data-active-step", String(n));
  }

  interpolar(peso, tabla, key) {
    const sorted = [...tabla].sort((a, b) => a.kg - b.kg);
    const min = sorted[0];
    const max = sorted[sorted.length - 1];

    if (peso <= min.kg) return { valor: min[key], clamp: { tipo: "min", valor: min.kg } };
    if (peso >= max.kg) return { valor: max[key], clamp: { tipo: "max", valor: max.kg } };

    for (let i = 0; i < sorted.length - 1; i++) {
      const a = sorted[i];
      const b = sorted[i + 1];
      if (peso >= a.kg && peso <= b.kg) {
        const t = (peso - a.kg) / (b.kg - a.kg);
        return { valor: Math.round(a[key] + t * (b[key] - a[key])), clamp: null };
      }
    }
    return { valor: 0, clamp: null };
  }

  /**
   * Parsea el peso aceptando coma decimal: en es-AR la gente escribe "4,2", no "4.2".
   * Con <input type="number"> el navegador descartaba ese valor y el campo quedaba vacío.
   */
  _parsePeso(value) {
    return parseFloat(String(value ?? "").trim().replace(",", "."));
  }

  /** Peso máximo contemplado por la tabla de la etapa elegida (ej. 60 kg para perro adulto). */
  _pesoMaximoTabla() {
    const tabla = this.data.tablas[this.state.etapa] ?? [];
    return tabla.reduce((max, r) => Math.max(max, Number(r.kg)), 0);
  }

  /**
   * Tope de sanidad: el doble del máximo de la tabla. Arriba de eso el número no es un peso
   * en kg sino casi siempre el peso en gramos (4200 en vez de 4,2). Sin este freno la
   * calculadora devolvía calladita el tope de la tabla —1100 g/día, 15 porciones— como si
   * fuera una recomendación para esa mascota.
   */
  _pesoLimiteSanidad() {
    return this._pesoMaximoTabla() * 2;
  }

  calcular() {
    const tabla = this.data.tablas[this.state.etapa];
    const key = this.state.etapa === "cachorro" ? this.state.edadCachorro : "g";
    const { valor, clamp } = this.interpolar(this.state.peso, tabla, key);
    this.state.gramosDia = valor;
    this.state.gramosTotal = valor * this.state.periodo;
    this.state.clampInfo = clamp;
  }

  async fetchProductos() {
    const handle = this.data.colecciones[this.state.etapa];
    if (!handle) return [];
    const res = await fetch(`/collections/${handle}/products.json?limit=20`, { credentials: "same-origin" });
    const data = await res.json();
    return data.products
      .map((p) => {
        const validVariants = p.variants.filter((v) => v.available && v.grams > 0);
        if (validVariants.length === 0) return null;
        const variant = [...validVariants].sort((a, b) => a.grams - b.grams)[0];
        return {
          id: p.id,
          handle: p.handle,
          title: p.title,
          vendor: p.vendor,
          image: p.images[0]?.src ?? "",
          price: variant.price,
          compareAtPrice: variant.compare_at_price,
          variant: { id: variant.id, weight: variant.grams },
        };
      })
      .filter(Boolean);
  }

  renderStep1() {
    const s = this.strings;
    const etapas = [
      { key: "perro_adulto", label: s.etapa_perro_label },
      { key: "gato_adulto", label: s.etapa_gato_label },
      { key: "cachorro", label: s.etapa_cachorro_label },
    ].filter((e) => this.data.colecciones[e.key]);

    const etapasHTML = etapas
      .map((e) => `<button type="button" class="calc-etapa-card" data-etapa="${e.key}">${escapeHTML(e.label)}</button>`)
      .join("");

    this.body.innerHTML = `
      <div class="calc-step" data-step="1">
        <h3>${escapeHTML(s.step1_titulo)}</h3>
        <div class="calc-etapas" data-etapas-count="${etapas.length}">
          ${etapasHTML}
        </div>
      </div>
    `;
    this.body.querySelectorAll("[data-etapa]").forEach((btn) => {
      btn.addEventListener("click", () => {
        this.state.etapa = btn.dataset.etapa;
        this.goToStep(2);
        this.renderStep2();
      });
    });
  }

  renderStep2() {
    const s = this.strings;
    const esCachorro = this.state.etapa === "cachorro";
    this.body.innerHTML = `
      <div class="calc-step" data-step="2">
        <div class="calc-step2-header">
          <h3>${escapeHTML(s.step2_titulo)}</h3>
          <span class="calc-step2-hint">${escapeHTML(s.step2_peso_label)}</span>
        </div>
        <input
          type="text"
          inputmode="decimal"
          autocomplete="off"
          placeholder="${escapeHTML(s.step2_peso_placeholder)}"
          class="calc-peso-input"
          data-calc-peso
          value="${this.state.peso ?? ""}"
        />
        <p class="calc-peso-error" data-calc-peso-error hidden></p>

        ${esCachorro ? `
          <h4>${escapeHTML(s.step2_edad_titulo)}</h4>
          <select class="calc-edad-select" data-calc-edad>
            <option value="">${escapeHTML(s.step2_edad_placeholder)}</option>
            <option value="g_2m" ${this.state.edadCachorro === "g_2m" ? "selected" : ""}>${escapeHTML(s.edad_2m_label)}</option>
            <option value="g_3_6m" ${this.state.edadCachorro === "g_3_6m" ? "selected" : ""}>${escapeHTML(s.edad_3_6m_label)}</option>
            <option value="g_6_12m" ${this.state.edadCachorro === "g_6_12m" ? "selected" : ""}>${escapeHTML(s.edad_6_12m_label)}</option>
          </select>
        ` : ""}

        <div class="calc-ctas">
          <button type="button" class="btn btn--primary btn--outline btn--large btn--full" data-calc-back>${escapeHTML(s.btn_volver)}</button>
          <button type="button" class="btn btn--primary btn--large btn--full" data-calc-next-step2 disabled>${escapeHTML(s.btn_siguiente)}</button>
        </div>
      </div>
    `;

    const pesoInput = this.body.querySelector("[data-calc-peso]");
    const edadSelect = this.body.querySelector("[data-calc-edad]");
    const nextBtn = this.body.querySelector("[data-calc-next-step2]");

    const errorEl = this.body.querySelector("[data-calc-peso-error]");
    const limite = this._pesoLimiteSanidad();

    const validar = () => {
      const peso = this._parsePeso(pesoInput.value);
      const edadOk = !esCachorro || (edadSelect && edadSelect.value);
      const fueraDeEscala = peso > 0 && limite > 0 && peso > limite;

      if (errorEl) {
        errorEl.textContent = fueraDeEscala
          ? template(s.peso_fuera_de_escala_template, { max: this._pesoMaximoTabla() })
          : "";
        errorEl.hidden = !fueraDeEscala;
      }

      nextBtn.disabled = !(peso > 0 && edadOk && !fueraDeEscala);
    };

    pesoInput.addEventListener("input", validar);
    if (edadSelect) edadSelect.addEventListener("change", validar);

    const goNext = () => {
      this.state.peso = this._parsePeso(pesoInput.value);
      if (esCachorro) this.state.edadCachorro = edadSelect.value;
      this.goToStep(3);
      this.renderStep3();
    };

    nextBtn.addEventListener("click", goNext);

    pesoInput.addEventListener("keydown", (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      if (!nextBtn.disabled) goNext();
    });

    this.body.querySelector("[data-calc-back]").addEventListener("click", () => {
      this.goToStep(1);
      this.renderStep1();
    });

    validar();
  }

  renderStep3() {
    const s = this.strings;
    this.body.innerHTML = `
      <div class="calc-step" data-step="3">
        <h3>${escapeHTML(s.step3_titulo)}</h3>
        <div class="calc-periodos">
          <button type="button" class="calc-periodo-card" data-periodo="7">${escapeHTML(s.periodo_7_label)}</button>
          <button type="button" class="calc-periodo-card" data-periodo="10">${escapeHTML(s.periodo_10_label)}</button>
          <button type="button" class="calc-periodo-card" data-periodo="15">${escapeHTML(s.periodo_15_label)}</button>
          <button type="button" class="calc-periodo-card" data-periodo="20">${escapeHTML(s.periodo_20_label)}</button>
          <button type="button" class="calc-periodo-card" data-periodo="30">${escapeHTML(s.periodo_30_label)}</button>
        </div>
        <div class="calc-ctas">
          <button type="button" class="btn btn--primary btn--outline btn--large btn--full" data-calc-back>${escapeHTML(s.btn_volver)}</button>
        </div>
      </div>
    `;

    this.body.querySelectorAll("[data-periodo]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        this.state.periodo = parseInt(btn.dataset.periodo, 10);
        this.calcular();
        this.goToStep(4);
        await this.renderStep4();
      });
    });

    this.body.querySelector("[data-calc-back]").addEventListener("click", () => {
      this.goToStep(2);
      this.renderStep2();
    });
  }

  async renderStep4() {
    const s = this.strings;
    this.body.innerHTML = `<div class="calc-step" data-step="4"><p>Cargando productos...</p></div>`;
    const productos = await this.fetchProductos();

    if (productos.length === 0) {
      this.body.innerHTML = `
        <div class="calc-step" data-step="4">
          <div class="calc-empty">
            <p>${escapeHTML(s.empty_state)}</p>
            <button type="button" class="btn btn--primary btn--outline btn--large" data-calc-restart>${escapeHTML(s.btn_volver_a_empezar)}</button>
          </div>
        </div>
      `;
      return;
    }

    this._renderStep4Content(productos);
  }

  _renderStep4Content(productos) {
    const s = this.strings;
    const detalleText = template(s.resumen_detalle_template, {
      dias: this.state.periodo,
      gramos: this.state.gramosTotal,
      kg: (this.state.gramosTotal / 1000).toFixed(2),
    });
    const porcionGramos = parseFloat(s.porcion_gramos) || 75;
    // Media porción hacia arriba: nunca sugerir menos comida que la ración diaria.
    const porcionesValor = Math.ceil((this.state.gramosDia / porcionGramos) * 2) / 2;
    const porcionesText = template(s.resumen_porciones_template, {
      porciones: porcionesValor.toLocaleString("es-AR"),
      gramos: porcionGramos,
    });
    const clampText = this.state.clampInfo
      ? template(
          this.state.clampInfo.tipo === "min" ? s.clamp_min_template : s.clamp_max_template,
          { kg: this.state.clampInfo.valor }
        )
      : null;

    this.body.innerHTML = `
      <div class="calc-step" data-step="4">
        <div class="calc-progress">
          <div class="calc-progress__bar"><div class="calc-progress__fill" data-calc-progress-fill style="width: 0%"></div></div>
          <p class="calc-progress__text" data-calc-progress-text></p>
        </div>

        <div class="calc-resumen">
          <p class="calc-resumen__principal">
            ${this.state.gramosDia} ${escapeHTML(s.resumen_dia_unit)}
            <span class="calc-resumen__porciones">(${escapeHTML(porcionesText)})</span>
          </p>
          <p class="calc-resumen__detalle">${escapeHTML(detalleText)}</p>
          ${clampText ? `<p class="calc-resumen__clamp">${escapeHTML(clampText)}</p>` : ""}
        </div>

        <div class="calc-productos" data-calc-productos>
          ${productos.map((p) => this._renderProductoCard(p)).join("")}
        </div>

        <div class="calc-cart-error" data-calc-error hidden></div>
        <div class="calc-ctas">
          <button type="button" class="btn btn--primary btn--outline btn--large btn--full" data-calc-add disabled>${escapeHTML(s.cta_agregar_carrito)}</button>
          <button type="button" class="btn btn--primary btn--large btn--full" data-calc-checkout disabled>${escapeHTML(s.cta_comprar)}</button>
        </div>
      </div>
    `;

    const productosContainer = this.body.querySelector("[data-calc-productos]");
    this.state.variantWeights = new Map(productos.map((p) => [p.variant.id, p.variant.weight]));
    this.state.variantTitles = new Map(productos.map((p) => [p.variant.id, p.title]));
    this.state.carritoLocal = new Map();

    this._wireQtySelectors();
    this._renderProgress();

    this.body.querySelector("[data-calc-add]").addEventListener("click", () => this._handleAdd(false));
    this.body.querySelector("[data-calc-checkout]").addEventListener("click", () => this._handleAdd(true));

    if (this._flickity) {
      this._flickity.destroy();
      this._flickity = null;
    }
    if (productos.length > 3) {
      productosContainer.classList.add("calc-productos--carousel");
      this._flickity = new Flickity(productosContainer, {
        cellAlign: "left",
        contain: true,
        pageDots: false,
        prevNextButtons: true,
        groupCells: 1,
        imagesLoaded: true,
        adaptiveHeight: true,
      });
    }
  }

  _renderProductoCard(p) {
    const weightKg = p.variant.weight / 1000;
    const weightLabel = weightKg % 1 === 0 ? weightKg.toString() : weightKg.toFixed(1);
    const priceFmt = `$${parseFloat(p.price).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    return `
      <div class="calc-producto" data-variant-id="${p.variant.id}">
        <div class="calc-producto__img-wrap">
          <img class="calc-producto__img" src="${escapeHTML(p.image)}" alt="${escapeHTML(p.title)}" loading="lazy" />
        </div>
        <div class="calc-producto__info">
          <p class="calc-producto__titulo">${escapeHTML(p.title)}</p>
          <p class="calc-producto__meta">
            <span class="calc-producto__weight">${weightLabel} kg</span>
            <span class="calc-producto__price">${escapeHTML(priceFmt)}</span>
          </p>
        </div>
        <div class="quantity__wrapper calc-producto__qty">
          <button type="button" class="quantity__button quantity__button--minus" data-calc-qty-minus aria-label="Quitar">${ICON_MINUS}</button>
          <input class="quantity__input" type="number" min="0" value="0" data-calc-qty-input />
          <button type="button" class="quantity__button quantity__button--plus" data-calc-qty-plus aria-label="Agregar">${ICON_PLUS}</button>
        </div>
      </div>
    `;
  }

  _wireQtySelectors() {
    this.body.querySelectorAll(".calc-producto").forEach((card) => {
      const input = card.querySelector("[data-calc-qty-input]");
      const plus = card.querySelector("[data-calc-qty-plus]");
      const minus = card.querySelector("[data-calc-qty-minus]");
      if (!input) return;
      const variantId = parseInt(card.dataset.variantId, 10);

      input.addEventListener("change", () => {
        const qty = Math.max(0, parseInt(input.value, 10) || 0);
        input.value = qty;
        this._setQty(variantId, qty);
      });
      if (plus) {
        plus.addEventListener("click", () => {
          const qty = (parseInt(input.value, 10) || 0) + 1;
          input.value = qty;
          this._setQty(variantId, qty);
        });
      }
      if (minus) {
        minus.addEventListener("click", () => {
          const qty = Math.max(0, (parseInt(input.value, 10) || 0) - 1);
          input.value = qty;
          this._setQty(variantId, qty);
        });
      }
    });
  }

  _setQty(variantId, qty) {
    if (qty <= 0) {
      this.state.carritoLocal.delete(variantId);
    } else {
      this.state.carritoLocal.set(variantId, qty);
    }
    this._renderProgress();
  }

  _renderProgress() {
    const totalAcumulado = [...this.state.carritoLocal.entries()].reduce((sum, [variantId, qty]) => {
      const weight = this.state.variantWeights.get(variantId) ?? 0;
      return sum + weight * qty;
    }, 0);

    const objetivo = this.state.gramosTotal;
    const percent = objetivo > 0 ? Math.min(100, Math.round((totalAcumulado / objetivo) * 100)) : 0;

    const fill = this.body.querySelector("[data-calc-progress-fill]");
    const text = this.body.querySelector("[data-calc-progress-text]");
    if (fill) {
      fill.style.width = `${percent}%`;
      fill.parentElement.parentElement.setAttribute("data-complete", percent >= 100 ? "true" : "false");
    }
    if (text) {
      text.textContent = template(this.strings.progress_text_template, {
        actual: (totalAcumulado / 1000).toFixed(1),
        total: (objetivo / 1000).toFixed(1),
      });
    }

    const hasItems = this.state.carritoLocal.size > 0;
    const addBtn = this.body.querySelector("[data-calc-add]");
    const checkoutBtn = this.body.querySelector("[data-calc-checkout]");
    if (addBtn) addBtn.disabled = !hasItems;
    if (checkoutBtn) checkoutBtn.disabled = !hasItems;
  }

  async _handleAdd(redirectToCheckout) {
    const items = [...this.state.carritoLocal.entries()].map(([id, quantity]) => ({ id, quantity }));
    const errorEl = this.body.querySelector("[data-calc-error]");
    errorEl.hidden = true;
    const ctas = this.body.querySelectorAll(".calc-ctas .btn");
    ctas.forEach((b) => (b.disabled = true));

    const result = await this._postAddToCart(items);

    if (!result.ok) {
      const productosLista = items
        .map(({ id, quantity }) => {
          const titulo = this.state.variantTitles.get(id) || "";
          return titulo ? `${titulo} (x${quantity})` : `(x${quantity})`;
        })
        .join(", ");
      const motivo = result.message || this.strings.error_message;
      const tmpl = this.strings.error_stock_template || "{motivo} Productos seleccionados: {productos}.";
      errorEl.textContent = template(tmpl, { productos: productosLista, motivo });
      errorEl.className = "calc-error";
      errorEl.hidden = false;
      ctas.forEach((b) => (b.disabled = false));
      return;
    }

    if (redirectToCheckout) {
      window.location.href = "/checkout";
      return;
    }

    const cartRes = await fetch("/cart.js", { credentials: "same-origin" });
    const cart = await cartRes.json();
    this.close();
    this.reset();
    document.dispatchEvent(new CustomEvent("theme:cart:change", { detail: { cart }, bubbles: true }));
    const drawerToggle = document.querySelector('[data-drawer-toggle="drawer-cart"]');
    if (drawerToggle) drawerToggle.click();
  }

  async _postAddToCart(items) {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items }),
    });
    if (res.ok) return { ok: true };
    const body = await res.json();
    return { ok: false, message: body.description || body.message };
  }
}

customElements.define("calculadora-raciones", CalculadoraRaciones);

document.addEventListener("click", (e) => {
  const trigger = e.target.closest('[data-calculadora-trigger], a[href="#calculadora-raciones"]');
  if (!trigger) return;
  if (trigger.tagName === "A") e.preventDefault();
  const popup = document.querySelector("calculadora-raciones");
  if (popup) popup.open();
});

document.addEventListener("keydown", (e) => {
  if (e.key !== "Escape") return;
  const popup = document.querySelector("calculadora-raciones");
  if (popup && !popup.hasAttribute("hidden")) popup.close();
});
